'use client';

import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

import { Volume2, VolumeX } from 'lucide-react';

import { CARDS } from '@/components/sections/customer-videos';
import { useTranslation } from '@/lib/i18n/context';
import { cn } from '@/lib/utils';

import { MicroLabel } from './story-card';
import { useStageHandoff } from './use-stage-handoff';

/**
 * Resting frame for a film, by the convention `scripts/video-posters.sh`
 * writes: `<name>.mp4` beside `<name>-poster.jpg`. Derived here rather than
 * imported so this component depends on nothing outside the customer-stories
 * work — a card carrying its own `poster` still wins.
 */
const posterFor = (videoSrc: string) =>
  videoSrc.replace(/\.mp4$/, '-poster.jpg');

/**
 * Full-height film stage at the top of /customer-stories.
 *
 * One film fills the viewport and the other two sit in a queue beside it;
 * when one ends the next starts, and the wall of stories begins below the
 * fold. The customer on camera is the only asset this page has that nothing
 * else on the site does, and a row of thumbnails was not doing it justice.
 *
 * Films and titles come from the same `CARDS` map `<CustomerVideos>` reads,
 * so the locale split comes for free: the Arabic interviews (Qoyod, OTO,
 * Syarah) lead /ar and must never surface on /en.
 *
 * Playback: the stage autoplays muted, because every browser blocks anything
 * else, and the centre control turns the sound on and restarts the film from
 * its first clean frame. `freezeAt` is that frame — past the lockup and the
 * caption that sit over the speaker at the start of each interview — so the
 * stage never rests on a title card.
 */

const LEAD_IDS: Record<'en' | 'ar', string[]> = {
  en: ['fareharbor', 'ticketswap', 'moneygram'],
  ar: ['qoyod', 'oto', 'syarah'],
};

type Card = (typeof CARDS)['en'][number];

const QueueItem = ({
  card,
  label,
  active,
  isRTL,
  onSelect,
}: {
  card: Card;
  label: string;
  active: boolean;
  isRTL: boolean;
  onSelect: () => void;
}) => (
  <button
    type="button"
    onClick={onSelect}
    // `--inner-radius` + `--inset` is the house concentric rule from
    // globals.css: the tile's corner equals the thumbnail's plus the padding
    // between them (8 + 8), so the two curves stay parallel instead of
    // pinching as they go round. Expressed as the variables rather than a
    // hardcoded 16px so changing the padding cannot silently break it.
    style={{ '--inner-radius': '8px', '--inset': '8px' } as React.CSSProperties}
    className={cn(
      'radius-concentric group flex w-full cursor-pointer items-center gap-3 border p-2 text-start backdrop-blur-sm transition-colors duration-300',
      active
        ? 'border-white/40 bg-white/15'
        : 'border-white/20 bg-black/30 hover:bg-white/10',
    )}
  >
    {/* The box owns the corner, not the image: globals.css forces
        `object-cover` media inside a rounded container to 0 on purpose —
        two rounded edges on the same pixels antialias into a dark fringe. */}
    <span className="radius-8 block h-12 w-16 shrink-0 overflow-hidden md:h-14 md:w-20">
      <img
        src={card.poster ?? posterFor(card.videoSrc)}
        alt=""
        className="size-full object-cover"
      />
    </span>
    <span className="flex min-w-0 flex-col gap-1">
      <MicroLabel isRTL={isRTL} className="text-white/60">
        {label}
      </MicroLabel>
      <span className="line-clamp-2 text-sm leading-[1.25] text-white">
        {card.title}
      </span>
    </span>
  </button>
);

export const StoriesVideoHero = ({
  titles,
  headlines,
  nowPlaying,
  playNext,
  watch,
}: {
  /** Localized titles, in LEAD_IDS order for the current locale. */
  titles: string[];
  /** Hero headline per film, in LEAD_IDS order — it changes with the film. */
  headlines: string[];
  nowPlaying: string;
  playNext: string;
  watch: string;
}) => {
  const { locale, isRTL } = useTranslation();
  const pool = locale === 'ar' ? CARDS.ar : CARDS.en;
  const cards = LEAD_IDS[locale === 'ar' ? 'ar' : 'en']
    .map((id) => pool.find((card) => card.id === id))
    .filter((card): card is Card => Boolean(card))
    .map((card, i) => ({
      ...card,
      title: titles[i] ?? card.title,
      headline: headlines[i] ?? titles[i] ?? card.title,
    }));

  const [index, setIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const ref = useRef<HTMLVideoElement>(null);
  const stageRef = useRef<HTMLElement>(null);
  const filmRef = useRef<HTMLDivElement>(null);
  const active = cards[index];
  const reduceMotion = useReducedMotion();

  // GSAP snaps between the stage and the wall, and scrubs the film back as
  // the wall arrives.
  useStageHandoff({ stage: stageRef, film: filmRef, enabled: !reduceMotion });

  /* The frame the outgoing film left behind. The incoming one wipes across
   * it, so the stage never flashes black between stories — without this the
   * new <video> mounts empty and paints its container colour until the first
   * frame decodes, which is the whole reason the swap looked abrupt. */
  const [outgoing, setOutgoing] = useState<string | null>(null);

  /* Which film the stage is showing. Derived during render rather than in an
   * effect: React's documented way to adjust state when an input changes, and
   * the only way to get the outgoing still on screen in the same paint as the
   * new film. */
  const [shown, setShown] = useState(active?.id);
  if (active && shown !== active.id) {
    const previous = cards.find((c) => c.id === shown);
    setShown(active.id);
    setOutgoing(
      previous ? (previous.poster ?? posterFor(previous.videoSrc)) : null,
    );
  }

  /* Dropped from the tree on the next tick. AnimatePresence keeps it mounted
   * to play `exit`, so this is what starts the wipe — the still is on its way
   * out from the moment the new film mounts. */
  useEffect(() => {
    if (!outgoing) return;
    const t = setTimeout(() => setOutgoing(null), 0);
    return () => clearTimeout(t);
  }, [outgoing]);

  // Each film starts at its own first clean frame rather than at 0, and the
  // stage keeps whatever sound setting the viewer last chose as it advances.
  // Written inline rather than through a useCallback: the compiler rejected
  // the memoized version, and it handles this for us anyway.
  const freezeAt = active?.freezeAt;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (freezeAt) el.currentTime = freezeAt;
    void el.play().catch(() => {});
  }, [index, freezeAt]);

  if (!active) return null;

  const advance = () => setIndex((i) => (i + 1) % cards.length);

  const toggleSound = () => {
    const el = ref.current;
    if (!el) return;
    const next = !muted;
    setMuted(next);
    el.muted = next;
    if (!next && el.paused) void el.play().catch(() => {});
  };

  return (
    <section
      ref={stageRef}
      // The bar floats transparent with white chrome while it is over this,
      // and reverts to dark-on-light the moment the wall below reaches it.
      data-nav-theme="film"
      className="relative h-[100svh] min-h-[560px] w-full overflow-hidden bg-neutral-950"
    >
      {/* Everything ScrollTrigger scrubs lives on one wrapper, so the pin
          transforms a single layer rather than each of the film, the still
          and the scrims separately. */}
      <div ref={filmRef} className="absolute inset-0">
        {/* The incoming film is always fully visible. The frame the previous
          one left behind sits ON TOP and wipes away to reveal it.

          That order matters for the failure case. Clipping the *incoming*
          film means anything that stops the animation — a throttled tab, a
          missed frame budget — leaves it clipped to nothing and the stage
          blank. Wiping the outgoing still away instead fails safe: the worst
          case is a still that lingers, and the timeout below removes it even
          if the transition never fires. */}
        <video
          ref={ref}
          key={active.id}
          src={active.videoSrc}
          poster={active.poster ?? posterFor(active.videoSrc)}
          autoPlay
          muted={muted}
          playsInline
          onEnded={advance}
          className="absolute inset-0 size-full object-cover"
          style={{ objectPosition: active.objectPosition ?? 'center' }}
        />

        {/* Motion drives the wipe as an `exit` on the outgoing frame. Exiting
          rather than entering is deliberate: clipping the *incoming* film in
          means anything that stops the animation leaves it clipped to nothing
          and the stage blank. Wiping the old frame off the top fails safe —
          the new film is already at full size underneath.

          `clip-path` interpolates between two `inset()` of the same shape and
          is compositor-friendly, so the wipe costs the scroll nothing. It
          opens from the leading edge, which flips with reading direction. */}
        <AnimatePresence>
          {outgoing ? (
            <motion.img
              key={outgoing}
              src={outgoing}
              alt=""
              aria-hidden
              className="absolute inset-0 size-full object-cover"
              initial={{ clipPath: 'inset(0% 0% 0% 0%)' }}
              exit={{
                clipPath: isRTL
                  ? 'inset(0% 100% 0% 0%)'
                  : 'inset(0% 0% 0% 100%)',
              }}
              transition={
                reduceMotion
                  ? { duration: 0 }
                  : { duration: 0.75, ease: [0.65, 0, 0.35, 1] }
              }
            />
          ) : null}
        </AnimatePresence>

        {/* Scrims: one along the bottom for the copy, one along the trailing
          edge for the queue. Without them the white type lands on whatever
          the film happens to be showing. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />
        <div
          className={cn(
            // Tailwind has no logical gradient direction, so the side scrim is
            // aimed at whichever edge the queue sits on for this locale.
            'pointer-events-none absolute inset-y-0 end-0 hidden w-1/3 from-black/70 to-transparent lg:block',
            isRTL ? 'bg-gradient-to-r' : 'bg-gradient-to-l',
          )}
        />
      </div>

      {/* House rule: nothing sits outside the logo's left edge or the Login
          link's right edge. Those are the borders, and `container` is what
          the navbar itself uses to set them — so the copy and the queue line
          up with the chrome above them rather than running wider. */}
      <div className="container relative z-10 flex h-full flex-col justify-end gap-8 pt-24 pb-10 lg:flex-row lg:items-end lg:justify-between lg:gap-12">
        <div className="flex max-w-xl flex-col gap-4">
          {/* The customer's own mark, where a section eyebrow would sit. It
              says whose story is playing better than the words "Success
              stories" did, and it changes with the film. */}
          <img
            src={active.logoSrc}
            alt={active.logoAlt}
            className="block max-h-9 w-auto max-w-[9rem] object-contain object-left brightness-0 invert"
          />
          {/* The headline belongs to the film, not to the page — it names
              whoever is on screen and changes as the queue advances. */}
          <h1 className="font-serif text-[2rem] leading-[1.1] text-white md:text-4xl xl:text-5xl">
            {active.headline}
          </h1>

          <div className="mt-2">
            <button
              type="button"
              onClick={toggleSound}
              className="flex cursor-pointer items-center gap-2 rounded-full bg-white px-4 py-2.5 text-sm font-medium text-black transition-transform duration-300 hover:scale-[1.03]"
            >
              {/* The icon reports the current state, not the action: muted
                  shows the crossed-out speaker. Written the other way round
                  it read as "there is sound", so pressing it to turn sound
                  ON looked like pressing it to mute. */}
              {muted ? (
                <VolumeX className="size-4" />
              ) : (
                <Volume2 className="size-4" />
              )}
              {watch}
            </button>
          </div>
        </div>

        <div className="flex w-full flex-col gap-2 lg:w-[22rem]">
          {cards.map((card, i) => (
            <QueueItem
              key={card.id}
              card={card}
              isRTL={isRTL}
              active={i === index}
              label={i === index ? nowPlaying : playNext}
              onSelect={() => setIndex(i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};
